<template>
  <div class="px-4 sm:px-6">
    <div class="min-h-screen py-16 bg-gray-type-5">
      <template v-if="fetchingBooking">
        <div class="flex items-center justify-center">
          <div class="my-40 spinner"></div>
        </div>
      </template>
      <template v-else>
        <template v-if="booking">
          <div class="flex flex-col lg:flex-row items-center max-w-3xl mx-auto">
            <div class="w-full lg:w-1/2">
              <div
                class="relative py-6 bg-white rounded-md text-dark-type-3 shadow-type-1"
              >
                <div class="absolute top-0 left-0 mt-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="32"
                  >
                    <g fill="none" fill-rule="evenodd">
                      <path
                        fill="#00e069"
                        d="M0 0h44c2.21 0 4 1.79 4 4v24c0 2.21-1.79 4-4 4H0V0z"
                      />
                      <g fill="#FFF" fill-rule="nonzero">
                        <path
                          d="M20.127 15.357v-2.552a.425.425 0 000-.851A2.13 2.13 0 0018 14.081v.85c0 .704.572 1.276 1.276 1.276h.85a.425.425 0 10.001-.85zm16.161-3.403a.425.425 0 000 .851v2.552a.425.425 0 000 .85h.85c.704 0 1.277-.572 1.277-1.276v-.85a2.13 2.13 0 00-2.127-2.127zM25.23 23.863a.425.425 0 00-.425.425h-2.552a.425.425 0 00-.851 0v.85c0 .704.573 1.277 1.276 1.277h1.702c.703 0 1.276-.573 1.276-1.276v-.85a.426.426 0 00-.426-.426zm9.357 0a.425.425 0 00-.425.425H31.61a.425.425 0 00-.851 0v.85c0 .704.573 1.277 1.276 1.277h1.701c.704 0 1.276-.573 1.276-1.276v-.85a.425.425 0 00-.425-.426z"
                        />
                        <path
                          d="M34.587 6h-12.76a2.129 2.129 0 00-2.125 2.127v14.46a2.13 2.13 0 002.127 2.127h12.759a2.13 2.13 0 002.127-2.127V8.127A2.13 2.13 0 0034.587 6zM23.529 7.701h9.357c.703 0 1.276.573 1.276 1.276 0 .704-.573 1.276-1.276 1.276h-9.357a1.278 1.278 0 01-1.276-1.276c0-.703.573-1.276 1.276-1.276zm0 14.46a1.277 1.277 0 01-1.276-1.275c0-.704.573-1.276 1.276-1.276.703 0 1.276.572 1.276 1.276 0 .703-.573 1.276-1.276 1.276zm9.357 0a1.277 1.277 0 010-2.551c.703 0 1.276.572 1.276 1.276 0 .703-.573 1.276-1.276 1.276zm2.126-5.528c0 .703-.572 1.276-1.276 1.276H22.678a1.278 1.278 0 01-1.276-1.276V12.38c0-.704.573-1.276 1.276-1.276h11.058c.704 0 1.276.572 1.276 1.276v4.253z"
                        />
                      </g>
                    </g>
                  </svg>
                </div>
                <div class="px-16 pb-8">
                  <div class="flex items-center justify-between">
                    <div class="font-bold">Shuttle tickets</div>
                  </div>
                  <div class="flex items-center justify-between mt-4 text-xs font-medium ">
                    <div class="text-dark-type-4">Ticket type</div>
                    <div class="text-dark-type-3">
                      {{ booking.recurring ? "Subscription" : "Single" }}
                    </div>
                  </div>
                  <template v-if="booking.recurring">
                    <div class="flex items-center justify-between mt-4 text-xs font-medium" >
                      <div class="text-dark-primary type-4">Trip days </div>
                      <div class="text-dark-primary type-3">{{tripDays}}</div>
                    </div>

                    <div class="flex items-center justify-between mt-4 text-xs font-medium" >
                      <div class="text-dark-primary type-4">Start day </div>
                      <div class="text-dark-primary type-3">{{booking.start_date | date('ddd, MMM DD YYYY')}}</div>
                    </div>
                    <div class="flex items-center justify-between mt-4 text-xs font-medium" >
                      <div class="text-dark-primary type-4">End day</div>
                      <div class="text-dark-primary type-3">{{booking.end_date | date('ddd, MMM DD YYYY')}}</div>
                    </div>
                    <div class="flex items-center justify-between mt-4 text-xs font-medium" v-if="booking.duration_in_weeks">
                      <div class="text-dark-primary type-4">Duration</div>
                      <div class="text-dark-primary type-3">{{ticketDuration}}</div>
                    </div>
                  </template>

                  <div class="flex items-center justify-between mt-4 text-xs font-medium mb-6">
                    <div class="text-dark-type-4">Ticket Status</div>
                    <div class="">
                      <span
                        class="
                          inline-flex
                          items-center
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xxs
                          font-medium
                        "
                        :class="{
                          'bg-yellow-100 text-yellow-800': booking.booking_status === 'pending',
                          'bg-green-100 text-green-800': booking.booking_status === 'active',
                        }"
                        >{{ booking.booking_status | titleCase }}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="bg-white text-dark-type-3 rounded-lg pt-4 pb-2 booking-details p-4" >
                  <div class="flex p-4 pb-6">
                    <div class="pt-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4C0 1.79086 1.79086 0 4 0Z" fill="#F3F7F5"/>
                        <circle cx="17" cy="16" r="10" fill="white" fill-opacity="0.01"/>
                        <path d="M10.3335 15.9999H22.0002" stroke="#C7D1CC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M17.8335 11.8333L22.0002 15.9999L17.8335 20.1666" stroke="#C7D1CC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div class="w-100">
                      <div class="flex justify-between px-4">
                        <div>
                          <label class="font-bold text-xs text-dark-type-3 leading-5">{{'Outbound trip'}}</label>
                          <p class="text-xs text-dark-type-3">{{ booking.start_date | date('ddd, MMM DD YYYY') }}</p>
                        </div>
                        <div class="flex items-center">
                          <div>
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4C0 1.79086 1.79086 0 4 0Z" fill="#F3F7F5"/>
                              <path d="M9.62021 15.129V13.1847C9.79909 13.1847 9.94427 13.0395 9.94427 12.8607C9.94427 12.6818 9.79909 12.5366 9.62021 12.5366C8.72652 12.5366 8 13.2638 8 14.1569V14.8049C8 15.3409 8.43615 15.7771 8.97213 15.7771H9.62021C9.79909 15.7771 9.94427 15.6319 9.94427 15.453C9.94427 15.2742 9.79909 15.129 9.62021 15.129Z" fill="#C7D1CC"/>
                              <path d="M21.9339 12.5366C21.755 12.5366 21.6099 12.6818 21.6099 12.8607C21.6099 13.0395 21.755 13.1847 21.9339 13.1847V15.129C21.755 15.129 21.6099 15.2742 21.6099 15.4531C21.6099 15.6319 21.755 15.7771 21.9339 15.7771H22.582C23.118 15.7771 23.5541 15.3409 23.5541 14.8049V14.1569C23.5541 13.2638 22.8276 12.5366 21.9339 12.5366Z" fill="#C7D1CC"/>
                              <path d="M13.5087 21.6099C13.3298 21.6099 13.1847 21.755 13.1847 21.9339H11.2404C11.2404 21.755 11.0952 21.6099 10.9163 21.6099C10.7375 21.6099 10.5923 21.755 10.5923 21.9339V22.582C10.5923 23.118 11.0284 23.5541 11.5644 23.5541H12.8606C13.3966 23.5541 13.8328 23.118 13.8328 22.582V21.9339C13.8328 21.755 13.6876 21.6099 13.5087 21.6099Z" fill="#C7D1CC"/>
                              <path d="M20.6376 21.6099C20.4587 21.6099 20.3135 21.755 20.3135 21.9339H18.3693C18.3693 21.755 18.2241 21.6099 18.0452 21.6099C17.8663 21.6099 17.7212 21.755 17.7212 21.9339V22.582C17.7212 23.118 18.1573 23.5541 18.6933 23.5541H19.9895C20.5255 23.5541 20.9616 23.118 20.9616 22.582V21.9339C20.9616 21.755 20.8165 21.6099 20.6376 21.6099Z" fill="#C7D1CC"/>
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M20.6379 8H10.9166C10.0229 8 9.29639 8.72716 9.29639 9.62021V20.6377C9.29639 21.5308 10.0229 22.258 10.9166 22.258H20.6379C21.5317 22.258 22.2582 21.5308 22.2582 20.6377V9.62021C22.2582 8.72716 21.5317 8 20.6379 8ZM12.2128 9.29619H19.3418C19.8778 9.29619 20.3139 9.73234 20.3139 10.2683C20.3139 10.8043 19.8778 11.2405 19.3418 11.2405H12.2128C11.6768 11.2405 11.2407 10.8043 11.2407 10.2683C11.2407 9.73234 11.6768 9.29619 12.2128 9.29619ZM11.2407 19.3416C11.2407 19.8775 11.6768 20.3137 12.2128 20.3137C12.7487 20.3137 13.1849 19.8775 13.1849 19.3416C13.1849 18.8056 12.7488 18.3694 12.2128 18.3694C11.6768 18.3694 11.2407 18.8056 11.2407 19.3416ZM19.3418 20.3137C18.8058 20.3137 18.3696 19.8775 18.3696 19.3416C18.3696 18.8056 18.8058 18.3694 19.3418 18.3694C19.8777 18.3694 20.3139 18.8056 20.3139 19.3416C20.3139 19.8775 19.8777 20.3137 19.3418 20.3137ZM19.9901 17.0732C20.5261 17.0732 20.9622 16.637 20.9622 16.101H20.9622V12.8606C20.9622 12.3246 20.526 11.8884 19.9901 11.8884H11.5649C11.0289 11.8884 10.5928 12.3246 10.5928 12.8606V16.101C10.5928 16.637 11.0289 17.0732 11.5649 17.0732H19.9901Z" fill="#C7D1CC"/>
                            </svg>
                          </div>
                          <span class="font-bold text-sm text-dark-type-3 leading-5 ml-1 text-right">{{driverData && driverData.vehicle ? driverData.vehicle.type : 'Bus'}}</span>
                        </div>
                      </div>
                      <div class="flex justify-between px-4 mt-4">
                        <div class="mx-30">
                          <p class="font-bold leading-normal text-sm">{{booking.itinerary.departure_time}}<span class="lowercase"> {{ booking.itinerary.time_of_day }}</span></p>
                          <p class="text-dark-type-4 text-xs">{{booking.pickup.location}}</p>
                        </div>
                        <div class="mx-30" style="align-self: center">
                          <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.3335 5.99992H13.0002" stroke="#737D78" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M8.8335 1.83325L13.0002 5.99992L8.8335 10.1666" stroke="#737D78" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                        <div class="mx-30">
                          <p class="font-bold leading-normal text-sm">{{arrivalTimeFormatted}}</p>
                          <p class="text-dark-type-4 text-xs">{{booking.destination.location}}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="p-4 border-gray-100 border-t border-solid">
                    <p class="flex justify-between align-center font-medium"><span class="text-xxs font-medium leading-3">Ticket Number</span> <span class="text-right text-xxs text-gray-500" style="max-width: 50%">{{booking.bus_pass}}</span></p>
                    <p class="flex justify-between align-center mt-1"><span class="text-xxs font-medium leading-3">Route</span> <span class="text-right text-xxs text-active" style="max-width: 50%">{{booking.route.pickup}}</span></p>
                    <p class="flex justify-between align-center font-medium mt-1"><span class="text-xxs font-medium leading-3">Route Code</span> <span class="text-right text-xxs text-gray-500" style="max-width: 50%">{{booking.route.route_code}}</span></p>

                    <div class="pt-10 flex justify-between items-center" v-if="driverData">
                      <div class="flex">
                        <div class="pr-4 relative flex items-center">
                          <img v-if="driverData.avatar" :src="driverData.avatar" class="driver-image">
                          <img v-else :src="require('@/assets/img/user_avatar.svg')">
                        </div>
                        <div class="ml-2">
                          <p class="text-sm font-bold leading-6">{{driverData.fname}} {{driverData.lname}}</p>
                          <p class="text-xs leading-3 text-gray-500">Driver</p>
                        </div>
                      </div>

                      <a href="tel:0700 070 0080" class="cursor-pointer decoration-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                          <g fill="none" fill-rule="evenodd">
                            <path
                                fill="#00e069"
                                d="M2.5 0h15A2.5 2.5 0 0120 2.5v15a2.5 2.5 0 01-2.5 2.5h-15A2.5 2.5 0 010 17.5v-15A2.5 2.5 0 012.5 0z"
                            />
                            <g fill="#FFF" transform="translate(3.75 3.75)">
                              <circle cx="6.25" cy="6.25" r="6.25" fill="none" />
                              <path
                                  fill-rule="nonzero"
                                  d="M9.398 7.423c-.459 0-.91-.072-1.337-.213a.611.611 0 00-.595.125l-.843.637c-.978-.522-1.58-1.124-2.095-2.095l.618-.821a.608.608 0 00.149-.615 4.262 4.262 0 01-.214-1.34.602.602 0 00-.602-.601H3.102a.603.603 0 00-.602.602A6.905 6.905 0 009.398 10c.332 0 .602-.27.602-.602V8.025a.603.603 0 00-.602-.602z"
                              />
                            </g>
                          </g>
                        </svg>
                      </a>
                    </div>
                  </div>

                  <div class="pb-0 px-4 pt-4 border-gray-100 border-t border-solid" v-if="driverData">
                    <p class="flex justify-between align-center"><span class="text-xxs font-medium leading-3">Vehicle Type</span> <span class="text-right text-xxs text-gray-500" style="max-width: 50%">{{vehicleName}}</span></p>
                    <p class="flex justify-between align-center mt-3" v-if="driverData.vehicle"><span class="text-xxs font-medium leading-3">Plate Number</span> <span class="text-right text-xxs text-gray-500" style="max-width: 50%">{{driverData.vehicle.registration_number || 'N/A'}}</span></p>
                  </div>

                  <div class="pb-0 px-4 pt-6" v-if="driverData && driverData.vehicle && driverData.vehicle.amenities">
                    <p class="flex justify-between align-center text-xs leading-5 font-bold">Bus Amenities</p>
                    <p class="flex justify-between align-center mt-3 text-xs" >
                      {{driverData.vehicle.amenities === 'AC' ? 'Air Contitioning' : driverData.vehicle.amenities}}
                    </p>
                  </div>
                </div>

                <div class="px-6 py-5">
                  <button
                    @click="openRouteLandingPage"
                    v-if="!isActiveTripBooking && hasValidUrl"
                    type="submit"
                    class="block w-full h-12 py-4 mt-12 font-bold text-center
                   bg-active text-black rounded focus:outline-none">
                    Book Again
                  </button>
                  <button
                    v-on:click="
                    booking.recurring
                      ? tripDaysModal(booking)
                      : deleteTicket(booking)
                  "
                    v-if="isActiveTripBooking"
                    type="submit"
                    class="block w-full h-12 py-4 mt-12 font-bold text-center text-white bg-red-500 rounded focus:outline-none"
                  >
                    Cancel booking
                  </button>
                </div>

                <modal
                  :show="showModalForTrips"
                  @close="showModalForTrips = false"
                  :key="componentKey"
                >
                  <div class="relative px-6 py-3 pb-6 text-dark-type-4">
                    <div class>
                      <div
                        class="cursor-pointer"
                        @click="showModalForTrips = false"
                      >
                        <svg
                          class="absolute"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                        >
                          <g fill="none" fill-rule="evenodd">
                            <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                            <path
                              d="M16 8l-8 8m0-8l8 8"
                              stroke="#131615"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="1.5"
                            />
                          </g>
                        </svg>
                      </div>
                      <div
                        class="mx-auto my-4 font-bold text-center text-dark-type-3"
                      >
                        Select Route Day to cancel
                      </div>
                      <div></div>
                      <p>These are the days you booked for on this route</p>
                      <div class="flex flex-wrap">
                        <div
                          class="mx-2"
                          v-for="(data, index) in this.subscribedTripDays"
                          :key="index"
                        >
                          <button
                            :class="
                              deleteDatesArray.includes(data)
                                ? 'btn_ bg-red-300 hover:bg-red-300 focus:outline-none btnLayoutPicked'
                                : 'btn_ hover:bg-red-300 focus:outline-none'
                            "
                            @click="toggleDate(data)"
                            :key="index"
                          >
                            {{ data }}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      @click="deleteTicket(booking)"
                      class="block w-full h-12 py-4 mt-8 mb-3 font-bold text-center text-white bg-red-300 rounded focus:outline-none"
                    >
                      Cancel Trip
                    </button>
                  </div>
                </modal>


              </div>
            </div>
            <div class="self-stretch w-full pt-4 lg:pt-0 lg:ml-4 bg-white rounded-md lg:w-1/2 min-h-200">
              <GmapMap
                ref="mapRef"
                :center="{ lat: 6.465422, lng: 3.406448 }"
                :zoom="11"
                :options="mapOptions"
                :streetViewControl="false"
                class="w-full h-full min-h-200"
                map-type-id="roadmap">
                <GmapMarker
                  :key="index"
                  v-for="(marker, index) in markers"
                  :position="marker.position"
                  :clickable="true"
                  :draggable="false"
                  :icon="{ url: require('@/assets/icons/bus.svg') }"
                  @click="center = marker.position"
                />
              </GmapMap>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center justify-center lg:py-20">
            <img
              src="@/assets/img/something-went-wrong.svg"
              alt="No transactions"
            />
            <div class="mt-10 text-2xl font-bold text-dark-type-3">
              Booking not found
            </div>
            <div class="mt-4 text-xs font-medium text-dark-type-4">
              This booking does not exist.
            </div>
            <router-link
              :to="{ name: 'dashboard.home' }"
              class="block w-56 py-2 py-4 mt-10 font-bold text-center text-white rounded bg-active"
              >Book a shuttle</router-link
            >
          </div>
        </template>
      </template>
    </div>
    <modal :show="showEditTicket">
      <div class="relative py-3 pb-6 text-dark-type-4">
        <div class="px-6 modal-header">
          <div class="cursor-pointer" @click="showEditTicket = false">
            <svg
              class="absolute"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
            >
              <g fill="none" fill-rule="evenodd">
                <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                <path
                  d="M16 8l-8 8m0-8l8 8"
                  stroke="#131615"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </div>
          <div class="mx-auto font-bold text-center">Edit Your Ticket</div>
        </div>
        <div class="mx-auto mt-3 text-center">
          <div class="w-full luggage-toggle">
            <label
              class="relative inline-block w-1/2 text-sm font-bold luggage_label"
            >
              Add Luggage
            </label>
            <div class="luggage-config-group">
              <input
                type="button"
                value="-"
                class="luggage-action"
                @click="
                  luggageValue > 1 ? (luggageValue = luggageValue - 1) : 1
                "
              />
              <input
                type="number"
                class="luggage-quantity counter"
                min="1"
                max="10"
                :value="luggageValue"
              />
              <input
                type="button"
                value="+"
                class="luggage-action"
                @click="
                  luggageValue >= 0 && luggageValue < 10
                    ? (luggageValue = luggageValue + 1)
                    : luggageValue
                "
              />
            </div>
          </div>
          <div class="content-center w-full centered">
            <button
              v-on:click="updateTicket(luggageValue)"
              type="submit"
              class="block w-1/3 h-12 py-4 mt-12 font-bold text-center text-white bg-red-500 rounded bg-active update-ticket focus:outline-none">
              Update Ticket
            </button>
          </div>
        </div>
      </div>
    </modal>
    <modal :show="showQrCode">
      <div class="relative py-3 pb-6 text-dark-type-4">
        <div class="px-6">
          <div class="cursor-pointer" @click="showQrCode = false">
            <svg
              class="absolute"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24">
              <g fill="none" fill-rule="evenodd">
                <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                <path
                  d="M16 8l-8 8m0-8l8 8"
                  stroke="#131615"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </div>
          <div class="mx-auto font-bold text-center text-dark-type-3">
            Your QR Code
          </div>
        </div>
        <div class="mx-auto mt-12 text-center">
          <div class="w-56 h-56 mx-auto"></div>
          <div class="mt-8 font-bold text-dark-type-3">
            How to use your QR code
          </div>
        </div>
        <div class="mt-4 bg-gray-type-5">
          <div class="flex px-6 py-4 pb-3">
            <div class="mt-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <g fill="none" fill-rule="evenodd" transform="translate(-7 -7)">
                  <circle cx="16" cy="16" r="16" fill="none" />
                  <path
                    stroke="#728881"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12.444 8H9.778C8.796 8 8 8.796 8 9.778v2.666m16 0V9.778C24 8.796 23.204 8 22.222 8h-2.666m0 16h2.666c.982 0 1.778-.796 1.778-1.778v-2.666m-16 0v2.666C8 23.204 8.796 24 9.778 24h2.666"
                  />
                </g>
              </svg>
            </div>
            <div class="ml-3">
              <div class="text-xs font-bold text-dark-type-3">Scan QR code</div>
              <div class="mt-1 font-medium text-xxs">
                Scan your QR code before entering the shuttle.
              </div>
            </div>
          </div>
        </div>
        <div class="px-6">
          <button class="block w-full h-12 py-4 mt-4 font-bold text-center text-white rounded bg-active">
            Download QR Code
          </button>
          <div class="w-3/4 mx-auto mt-2 font-medium text-center text-xxs text-dark-type-3">
            Unless on subscription basis, this QR code is only valid for this
            trip and cannot be reused.
          </div>
        </div>
      </div>
    </modal>
    <modal :show="showBusDetails && booking" @close="showBusDetails = false">
      <div class="relative px-6 py-3 pb-6 text-dark-type-4" v-if="booking">
        <div class>
          <div class="cursor-pointer" @click="showBusDetails = false">
            <svg
              class="absolute"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
            >
              <g fill="none" fill-rule="evenodd">
                <circle cx="12" cy="12" r="12" fill="#FCFCFC" />
                <path
                  d="M16 8l-8 8m0-8l8 8"
                  stroke="#131615"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                />
              </g>
            </svg>
          </div>
          <div class="mx-auto font-bold text-center text-dark-type-3">
            Bus details
          </div>
        </div>
        <div class="flex items-center justify-between mt-8">
          <div class="font-bold text-dark-type-3" v-if="booking.driver">

            {{ driverData.fname }} {{ driverData.lname }}
          </div>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
              <g fill="none" fill-rule="evenodd">
                <path
                  fill="#00e069"
                  d="M2.5 0h15A2.5 2.5 0 0120 2.5v15a2.5 2.5 0 01-2.5 2.5h-15A2.5 2.5 0 010 17.5v-15A2.5 2.5 0 012.5 0z"
                />
                <g fill="#FFF" transform="translate(3.75 3.75)">
                  <circle cx="6.25" cy="6.25" r="6.25" fill="none" />
                  <path
                    fill-rule="nonzero"
                    d="M9.398 7.423c-.459 0-.91-.072-1.337-.213a.611.611 0 00-.595.125l-.843.637c-.978-.522-1.58-1.124-2.095-2.095l.618-.821a.608.608 0 00.149-.615 4.262 4.262 0 01-.214-1.34.602.602 0 00-.602-.601H3.102a.603.603 0 00-.602.602A6.905 6.905 0 009.398 10c.332 0 .602-.27.602-.602V8.025a.603.603 0 00-.602-.602z"
                  />
                </g>
              </g>
            </svg>
          </div>
        </div>
        <div class="flex items-center justify-between mt-2">
          <div class="text-xs font-medium text-dark-type-4">Bus driver</div>
          <div
            class="text-xs font-medium text-dark-type-4"
            v-if="booking.route.driver"
          >
            07000700080
          </div>
        </div>
        <hr class="mt-6 border-gray-type-2" />
        <div class="mt-8 text-sm font-bold text-dark-type-3">Bus amenities</div>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal";
import axios from "@/services/axios";
import moment from "moment";

export default {
  props: ["bookingId"],
  components: {
    Modal,
  },
  data() {
    return {
      bookingEndDate: null,
      currentDate: null,
      showQrCode: false,
      showEditTicket: false,
      luggageValue: 1,
      shouldUseLuggage: null,
      totalLuggageQuantity: 0,
      showBusDetails: false,
      fetchingBooking: true,
      booking: null,
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
      },
      markers: [],
      subscribedTripDays: null,
      showModalForTrips: false,
      deleteDatesArray: [],
      componentKey: 0,
      holderArray: [],
    };
  },
  created() {
    if(this.bookingId) {
      axios.get(`user-routes/${this.bookingId}`).then((response) =>  {
        if(response.status === 200) {
          this.booking = response.data;
        }
      }).catch((e) => {
        console.error(e);
      }).finally(() => {
        this.fetchingBooking = false;
        this.setPageData();
        this.renderRouteAndMarkers();
      });
    } else {
      this.fetchingBooking = false
    }
  },
  computed: {
    ...mapGetters("app", ["bookings"]),
    driverData() {
      return this.booking && this.booking.route && this.booking.route.driver ? this.booking.route.driver : null;
    },
    vehicleName() {
      if(this.driverData && this.driverData.vehicle) {
        const {brand, name, type} = this.driverData.vehicle;
        return `${brand} ${name} - ${type}`;
      }
      return 'N/A';
    },
    arrivalTimeFormatted() {
      if(this.booking && this.booking.route && this.booking.itinerary) {
        const [ route_duration, departure_time, time_of_day ] = [this.booking.route.duration, this.booking.itinerary.departure_time, this.booking.itinerary.time_of_day];
        let durationParts = route_duration.split(" ");
        let duration = parseInt(durationParts[0]);
        if (durationParts[1] === "hour") {
          duration = duration * 60;
        }
        return moment(`${departure_time} ${time_of_day}`, "h:mm A")
            .add(duration, "m")
            .format("h:mm a");
      } else  {
        return '';
      }

    },
    isActiveTripBooking() {
      return this.bookingEndDate ?
          this.booking.booking_status === "active" && this.bookingEndDate >= this.currentDate
          : this.booking.booking_status === "active";
    },
    tripDays() {
      if(this.booking && this.booking.selected_days) {
        const uniqueDays = new Set(this.booking.selected_days || []);
        return this.booking.selected_days.length ? Array.from(uniqueDays).map((obj) => obj.slice(0,3)).join(', ') : '';
      }
      return '';
    },
    ticketDuration() {
      return `${this.booking.duration_in_weeks} week${this.booking.duration_in_weeks > 1 ? 's': ''}`
    },
    routePageUrl() {
      return `${process.env.VUE_APP_WEBSITE_URL}/routes/${this.booking.route.slug}`;
    },
    hasValidUrl() {
      return !!this.booking?.route?.slug;
    }
  },
  methods: {
    openRouteLandingPage() {
      if(this.hasValidUrl) {
        window.open(this.routePageUrl);
      }
    },
    renderRouteAndMarkers() {
      if(this.booking){
        this.$nextTick(() => {
          this.setMapMarkers();
          this.$refs.mapRef.$mapPromise.then((map) => {
            this.drawRoute(map);
            const routeBounds = JSON.parse(this.booking.route.bounds);
            const bounds = new window.google.maps.LatLngBounds(
                routeBounds.southwest,
                routeBounds.northeast
            );
            map.fitBounds(bounds);
          });
        });
      }
    },
    setPageData() {
      if(this.booking) {
        if (Object.prototype.hasOwnProperty.call(this.booking, "luggage_quantity")) {
          this.luggageValue = this.booking.luggage_quantity ? this.booking.luggage_quantity : 0;
        }
        this.shouldUseLuggage = this.booking.route.support_luggage;
        const endOfDay = (date) => {
          const eod = new Date(date);
          if( eod && eod !== "Invalid Date") {
            //const newDate =  new Date(`${eod}T23:59:59`);
            const newDate =  new Date(`${date}T23:59:59`);
            newDate.setDate(eod.getDate());
            return newDate.getTime();
          } else {
            return null;
          }
        };
        if(this.booking.end_date) {
          this.bookingEndDate = endOfDay(this.booking.end_date);
        }
        this.currentDate = new Date().getTime();
        if (this.bookingEndDate === "Invalid date") {
          this.bookingEndDate = null;
        }
      }
    },
    setMapMarkers() {
      const [pickupLat, pickupLng] =
        this.booking.route.pickup_coordinate.split(",");
      const [destinationLat, destinationLng] =
        this.booking.route.destination_coordinate.split(",");

      this.markers.push(
        {
          position: { lat: parseFloat(pickupLat), lng: parseFloat(pickupLng) },
        },
        {
          position: {
            lat: parseFloat(destinationLat),
            lng: parseFloat(destinationLng),
          },
        }
      );
    },
    drawRoute(map) {
      const encodedPolyline = JSON.parse(this.booking.route.overview_polyline);

      var flightPath = new window.google.maps.Polyline({
        path: window.google.maps.geometry.encoding.decodePath(
          encodedPolyline.points
        ),
        geodesic: true,
        strokeColor: "#00e069",
        strokeOpacity: 1.0,
        strokeWeight: 3,
      });

      flightPath.setMap(map);
    },
    deleteTicket(booking) {
      const { itinerary_id, route_id, pickup_id, destination_id, user_id } =
        booking;
      let data = { itinerary_id, route_id, pickup_id, destination_id, user_id };

      if (booking.recurring) {
        if (!this.deleteDatesArray.length) {
          return this.$toast.error("Please select trip days to cancel");
        }
        data.booked_days = this.deleteDatesArray;
      }

      if (window.confirm("Are you sure you want to cancel this booking?")) {
        return axios
          .delete(`users/${user_id}/routes`, { data: data })
          .then((res) => {
            if (res.status === 200) {
              window.alert("Ticket deleted successfully");
              return window.location.assign("/dashboard/home");
            }
            return window.location.assign("/dashboard/home");
          })
          .catch((error) => {
            if (error.response.data && error.response.data.message) {
              return this.$toast.error(error.response.data.message);
            }
            return this.$toast.error(
              "An error occurred while cancelling booking, please try again later"
            );
          });
      } else {
        return;
      }
    },
    updateTicket(luggageValue) {
      const data = {
        user_route_id: this.booking.id,
        luggage_quantity: luggageValue,
      };
      return axios
        .patch(`users/${this.booking.user_id}/luggage`, data)
        .then((res) => {
          if (res.status === 200) {
            this.showEditTicket = false;
            this.$toast.success(`Ticket successfully updated with luggage`);
          }
        })
        .catch((error) => {
          if (error.response.data && error.response.data.message) {
            return this.$toast.error(error.response.data.message);
          }
          return this.$toast.error(
            "An error occurred while updating booking with luggage, please try again later"
          );
        });
    },
    toggleDate(date) {
      const format_ = "YYYY-MM-DD";
      let today = moment(new Date()).format(format_);

      if (today > date) {
        return this.$toast.error("Sorry, cannot select a past date");
      }

      if (this.deleteDatesArray.includes(date)) {
        for (var i = 0; i < this.deleteDatesArray.length; i++) {
          if (this.deleteDatesArray[i] === date) {
            this.deleteDatesArray.splice(i, 1);
            i--;
          }
        }
      } else {
        this.deleteDatesArray.push(date);
      }
    },
    tripDaysModal(booking) {
      return axios
        .get(`users/routes/user_route_id/${booking.id}`)
        .then((res) => {
          if (res.status === 200) {
            this.subscribedTripDays = res.data.map((x) => x.trip_date);
            this.showModalForTrips = true;
          }
        })
        .catch((error) => {
          if (error.response.data && error.response.data.message) {
            return this.$toast.error(error.response.data.message);
          }
          return this.$toast.error(
            "An error occurred while cancelling booking, please try again later"
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.rowDiv {
  display: flex;
  flex-flow: row;
  display: grid;
  grid-template-columns: repeat(3, 30%);
  width: 100%;
  margin: 0 4%;
  transition: all 0.7s ease-in;
}
.btn_,
.btnLayoutPicked {
  height: 45px;
  width: 120px;
  margin: 2%;
  border-radius: 10px;
}
.btnLayout {
  width: 100px;
  margin: 5%;
}
.btn_:hover {
  color: white;
}
.btnLayoutPicked {
  color: white;
}
.ticket-edit {
  margin-top: 10%;
}
.luggage-quantity {
  border: 1px solid #737d78;
}
.centered {
  margin: auto;
}
.update-ticket {
  margin: auto;
  margin-top: 30px;
}
.counter {
  padding-left: 8%;
  width: 20%;
}

.booking-details {
  position: relative;

  &:not(:first-of-type) {
    background-image: linear-gradient(to right, theme('colors.gray.type-7') 33%, rgba(255,255,255,0) 0%);
    background-size: 1.25rem 1px;
    background-repeat: repeat-x;
    border-radius: 0 0 0.5rem 0.5rem;

    &:before, &:after {
      content:" ";
      position:absolute;
      width:24px;
      height:24px;
      border-radius:50%;
      @apply bg-gray-type-5;
    }

    &:before {
      box-shadow: inset -3px 0px 4px -2px rgb(0 0 0 / 8%);
      left: -12px;
      top: -12px;
    }

    &:after {
      box-shadow: inset 3px 0px 4px -2px rgb(0 0 0 / 8%);
      right: -12px;
      top: -12px;
    }
  }
  &:not(:last-of-type) {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  & .driver-image {
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
  }

  .w-100 {
    width: 100%;
  }

}

.min-h-200 {
  min-height: 12.5rem;
}
</style>
